import { Routes, Route, useNavigate, useParams, useSearchParams, Outlet } from 'react-router-dom'
import { toast } from 'react-toastify'
import React from 'react'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import ListItemButton from '@mui/material/ListItemButton'
import Divider from '@mui/material/Divider'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Chip from '@mui/material/Chip'
import ComputerIcon from '@mui/icons-material/Computer'
import ArticleIcon from '@mui/icons-material/Article'

import { useQuery } from '~/client/hooks/cqrpc'
import { useCurrentUser } from '~/client/hooks/auth'
import ErrorMessage from '~/client/components/ErrorMessage'
import Link from '~/client/components/Link'
import Loading from '~/client/components/Loading'
import ButtonRow from '~/client/components/ButtonRow'
import CommandForm from '~/client/components/CommandForm'
import LoadingList from '~/client/components/LoadingList'
import Heading from '~/client/components/Heading'
import Timestamp from '~/client/components/Timestamp'
import AppAccountLoginButton from '~/client/components/AppAccountLoginButton'
import AvatarAndNameInputs from '~/client/components/AvatarAndNameInputs'
import EmptyMessage from '~/client/components/EmptyMessage'
import UnshareDocumentButton from '~/client/components/UnshareDocumentButton'


export default function Page() {
  const {id} = useParams()
  const appAccountQuery = useQuery('appAccounts.getById', {id})
  if (appAccountQuery.loading) return <Loading/>
  if (appAccountQuery.error) return <ErrorMessage error={appAccountQuery.error}/>
  const appAccount = appAccountQuery.result
  if (!appAccount) return <div>not found</div>
  return <Container><Show {...{ appAccount }}/></Container>
}

function Show({ appAccount }){
  const { id, app } = appAccount

  return <Paper sx={{
    my: 2, p: 2, maxWidth: 'sm', mx: 'auto',
  }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 2}}>
      <Heading>{appAccount.app.name}</Heading>
      <ButtonRow>
        <AppAccountLoginButton appAccountId={id}/>
      </ButtonRow>
    </Stack>

    <Stack sx={{mb:2}}>
      <Box>App: <Link to={`/apps/${app.host}`}>{app.name}</Link></Box>
      <Box>Host: <Link to={app.homepageUrl}>{app.host}</Link></Box>
      <Box>Created: <Timestamp at={appAccount.createdAt}/></Box>
    </Stack>

    <Box>
      <Heading variant="sub" underlined>Sessions</Heading>
      <AppAccountSessionsList {...{appAccountId: id}}/>
    </Box>

    <Box>
      <Heading variant="sub" underlined>Data Blocks Shared</Heading>
      <AppAccountDocumentsList {...{appAccountId: id}}/>
    </Box>

    <Heading variant="sub" underlined>Danger Zone</Heading>
    <ButtonRow sx={{mt: 2}}>
      <Button variant="outlined" color="error">Delete all my data</Button>
      <Button variant="outlined" color="error">Destroy account</Button>
    </ButtonRow>

    {/*<InspectObject object={query.result}/>*/}
  </Paper>
}

function LoginToHost({ host, app }) {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { result: appAccounts, loading, error } = useQuery('appAccounts.forHost', { host })

  React.useEffect(
    () => { if (appAccounts?.length === 0) navigate(`/accounts/${host}/new`) },
    [appAccounts?.length]
  )

  return <Paper sx={{
     p: 2, maxWidth: 'sm', margin: 'auto',
  }}>
    <Heading>Login to {app.name}</Heading>
    {(loading || !appAccounts?.length) ? <CircularProgress/> : null}
    {appAccounts?.length > 0
      ? <>
        <Heading variant="sub">You have {appAccounts.length} accounts</Heading>
        <AppAccountsList {...{appAccounts}}/>
        <Divider>OR</Divider>
        <Stack>
          <CreateNewAppAccountButton host={host}/>
        </Stack>
      </>
      : null
    }
  </Paper>
}


function AppAccountsList({ appAccounts }){
  const { currentUser } = useCurrentUser()
  return <List spacing={1} sx={{mb: 2}}>
    {appAccounts.map(appAccount =>
      <ListItem key={appAccount.id} disablePadding>
        <ListItemIcon>
          {/*<FingerprintIcon/>*/}
          {/*<AppAccountAvatar {...{ appAccount }}/>*/}
        </ListItemIcon>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{width: '100%'}}>
          <ListItemText
            primary={
              <Link to={`/accounts/${appAccount.app.host}/${appAccount.id}`}>
                {appAccount.displayName || appAccount.id}
              </Link>
            }
            secondary={<>Created: <Timestamp at={appAccount.createdAt}/></>}
          />
          <AppAccountLoginButton accountId={appAccount.id}/>
        </Stack>
      </ListItem>
    )}
  </List>
}


function AppAccountsAvatarAndNameInputs({
  avatar,
  onAvatarChange,
  displayName,
  onDisplayNameChange,
}){
  const { currentUser } = useCurrentUser()
  return <AvatarAndNameInputs {...{
    avatar: avatar || currentUser.avatar || '',
    onAvatarChange,
    displayName: displayName ?? currentUser.displayName ?? '',
    onDisplayNameChange,
  }}/>
}



function CreateNewAppAccountButton({ host, children, ...props }) {
  return <Button
    variant="text"
    color="primary"
    component={Link}
    to={`/accounts/${host}/new`}
    {...props}
  >{children || `Create a new account`}</Button>
}


function AppAccountSessionsList({ appAccountId }){
  const query = useQuery('appAccounts.getSessions', { appAccountId })
  if (query.loading) return <Loading/>
  if (query.result.length === 0)
    return <EmptyMessage sx={{my: 2}}>This account has no active sessions</EmptyMessage>
  return <List>
    {query.result.map(session =>
      <ListItem key={session.id} sx={{p: 0}}>
        <ListItemIcon>
          <ComputerIcon/>
        </ListItemIcon>
        <ListItemText
          primary="Los Angeles 98.97.140.148"
          secondary={<>
            {`Last accessed on `}<Timestamp at={session.createdAt}/>
          </>}
        />
        <CommandForm {...{
          renderError: false,
          formButtons: false,
          command: 'appAccounts.revokeSession',
          onSubmit(value, command) {
            command.call({ sessionId: session.id })
          },
          onFailure(error){
            toast.error(`${error}`)
          },
          onSuccess(){
            query.reload()
            toast.success('Session revoked!')
          },
        }}>{({ submitting }) => (
          <Button
            type="submit"
            variant="outlined"
            color="error"
          >{submitting ? 'REVOKING…' : 'REVOKE'}</Button>
        )}</CommandForm>
      </ListItem>
    )}
  </List>
}



function AppAccountDocumentsList({ appAccountId }){
  const query = useQuery('appAccounts.getDocumentPermissions', { appAccountId })
  if (query.loading) return <Loading/>
  if (query.error) return <ErrorMessage error={query.error}/>
  if (query.result.length === 0)
    return <EmptyMessage sx={{my: 2}}>No Documents have been shared</EmptyMessage>
  return <List sx={{p: 0}}>
    {query.result.map(documentShare =>
      <ListItem key={documentShare.id} sx={{p: 0}}>
        <ListItemIcon>
          <ArticleIcon/>
        </ListItemIcon>
        <ListItemText
          primary={
            <Link to={`/documents/${documentShare.documentId}`}>
              {documentShare.document.name}
            </Link>
          }
          title={documentShare.document.id}
          secondary={<>
            {`Shared on `}<Timestamp at={documentShare.createdAt}/>
          </>}
        />
        <UnshareDocumentButton {...{
          appAccountId,
          documentId: documentShare.document.id,
          onSuccess: () => query.reload(),
        }}/>
      </ListItem>
    )}
  </List>
}
